import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/authContext';
import { listChatArtifacts } from '../lib/api/chatArtifactApi.connect';
export function useArtifacts(options = {}) {
    const { limit = 50, offset = 0, kind, sortBy = 'recent', search } = options;
    const { user } = useAuth();
    const [artifacts, setArtifacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const isMounted = useRef(true);
    const fetchArtifacts = useCallback(async ()=>{
        if (!user) {
            setError('User not authenticated');
            setLoading(false);
            return;
        }
        try {
            // Connect RPC through sabaody. Tenant and owner come from the gateway
            // principal, so no X-Tenant-ID header and no owner parameter — the server
            // will not accept a caller-supplied owner.
            const data = await listChatArtifacts({
                limit,
                offset,
                kind,
                sortBy,
                search
            });
            if (!isMounted.current) return;
            setArtifacts(data.artifacts);
            setTotal(data.total);
            setHasMore(data.hasMore);
            setError(null);
        } catch (err) {
            if (!isMounted.current) return;
            const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
            setError(errorMessage);
            console.error('Error fetching artifacts:', err);
        } finally{
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [
        user,
        limit,
        offset,
        kind,
        sortBy,
        search
    ]);
    // Drop a just-deleted row from the displayed list. The delete is already
    // confirmed server-side (this runs in the mutation's onSuccess) and durable
    // (a deleted id can't be resurrected), so this is authoritative — no
    // reconciling refetch, which would only risk re-adding the row on a lagging
    // list read.
    //
    // Presence is checked OUTSIDE the setState updaters (so total decrements only
    // for a row that was actually present) and both dispatches are top-level — a
    // setState side-effect nested inside another setState's updater double-fires
    // under StrictMode, which double-invokes updaters.
    const removeArtifact = useCallback((id)=>{
        if (!artifacts.some((a)=>a.id === id)) return;
        setArtifacts((prev)=>prev.filter((a)=>a.id !== id));
        setTotal((t)=>Math.max(0, t - 1));
    }, [
        artifacts
    ]);
    // Initial fetch
    useEffect(()=>{
        isMounted.current = true;
        fetchArtifacts();
        return ()=>{
            isMounted.current = false;
        };
    }, [
        fetchArtifacts
    ]);
    return {
        artifacts,
        loading,
        error,
        total,
        hasMore,
        refetch: fetchArtifacts,
        removeArtifact
    };
}
